import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import AssetUploader from "@/components/AssetUploader";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, IdentificationBadge, Printer, Sparkle, Browser, MegaphoneSimple, Palette, Devices, ShieldStar } from "@phosphor-icons/react";

const CATEGORY_META = {
  "Logo & Branding": { icon: IdentificationBadge, tint: "bg-[#FFE7D3]" },
  "Print Design": { icon: Printer, tint: "bg-[#F7E2F7]" },
  "Social Media Graphics": { icon: Sparkle, tint: "bg-[#E7F0FF]" },
  "Website Design": { icon: Browser, tint: "bg-[#DFF7E7]" },
  "Promotional Campaigns": { icon: MegaphoneSimple, tint: "bg-[#FFF6C4]" },
  "Business Stationery": { icon: Palette, tint: "bg-[#E3DEFC]" },
  "Signage & Display": { icon: Devices, tint: "bg-[#FFDCDC]" },
  "Print-Ready Artwork": { icon: ShieldStar, tint: "bg-[#D0EFEA]" },
};

const QUESTIONS = {
  "Logo & Branding": [
    { key: "company_name", label: "Company / brand name" },
    { key: "industry", label: "Industry / niche" },
    { key: "audience", label: "Target audience" },
    { key: "vision", label: "Vision, values & personality (3-5 words)" },
    { key: "competitors", label: "Competitors or brands you admire (URLs)" },
    { key: "must_have_elements", label: "Must-have elements (symbols, colors, typography)" },
    { key: "existing_assets", label: "Existing logo / brand assets to build upon?" },
    { key: "deliverables", label: "Deliverables required (logo, colour palette, guidelines, etc.)" },
    { key: "deadline", label: "Deadline / launch date" },
    { key: "budget", label: "Budget range" },
  ],
  "Print Design": [
    { key: "product", label: "What are we designing? (leaflet, brochure, poster…)" },
    { key: "size_format", label: "Size / format" },
    { key: "quantity", label: "Print quantity" },
    { key: "purpose", label: "Purpose / campaign context" },
    { key: "content_ready", label: "Is copy & imagery ready?" },
    { key: "brand_guidelines", label: "Brand guidelines / colours / fonts" },
    { key: "delivery_date", label: "Required delivery date" },
    { key: "budget", label: "Budget range" },
  ],
  "Social Media Graphics": [
    { key: "platforms", label: "Which platforms (Instagram, LinkedIn, Facebook, TikTok…)" },
    { key: "campaign_theme", label: "Campaign theme / goals" },
    { key: "post_count", label: "How many posts / stories?" },
    { key: "duration", label: "Duration / posting schedule" },
    { key: "brand_guidelines", label: "Brand guidelines / assets provided" },
    { key: "cta", label: "Primary call-to-action" },
    { key: "deadline", label: "Deadline" },
  ],
  "Website Design": [
    { key: "company_name", label: "Company / organisation name" },
    { key: "industry", label: "Industry / niche" },
    { key: "primary_contacts", label: "Primary contacts & their roles" },
    { key: "main_goal", label: "Main goal of the website (leads, awareness, info) + priority CTAs / metrics" },
    { key: "target_audience", label: "Target audience (demographics, industry)" },
    { key: "features", label: "Features needed (e-commerce, booking, blog, etc.)" },
    { key: "brand_guidelines", label: "Brand guidelines, colours, typography, existing assets, styles to incorporate" },
    { key: "content_ready", label: "Do you have content ready (text, images, videos)?" },
    { key: "content_owner", label: "Who will generate and maintain content?" },
    { key: "updates_maintenance", label: "How will website updates / maintenance be handled?" },
    { key: "admired_sites", label: "Websites you admire (URLs)" },
    { key: "style_feel", label: "Desired feel/style (modern, corporate, playful…)" },
    { key: "functionalities", label: "Specific functionalities (forms, search, member login…)" },
    { key: "navigation", label: "How do you envision the navigation structure?" },
    { key: "integrations", label: "Integrations required (CRM, e-commerce, etc.)" },
    { key: "domain_hosting", label: "Do you have a domain and hosting plan?" },
    { key: "technologies", label: "Preferred CMS / frameworks" },
    { key: "budget", label: "Budget for the project" },
    { key: "timeline", label: "Timeline / key dates" },
    { key: "extra_requirements", label: "Any other requirements or features?" },
    { key: "decision_maker", label: "Final decision maker" },
  ],
  "Promotional Campaigns": [
    { key: "goal", label: "Campaign goal" },
    { key: "target_audience", label: "Target audience" },
    { key: "channels", label: "Channels (print, digital, social, email…)" },
    { key: "duration", label: "Duration & key dates" },
    { key: "deliverables", label: "Deliverables required" },
    { key: "brand_guidelines", label: "Brand guidelines" },
    { key: "budget", label: "Budget" },
  ],
  "Business Stationery": [
    { key: "items", label: "Items required (business cards, letterhead, compliment slips…)" },
    { key: "quantity", label: "Quantities" },
    { key: "brand_assets", label: "Do you have logo / brand assets?" },
    { key: "finish", label: "Preferred paper / finish / lamination" },
    { key: "deadline", label: "Deadline" },
  ],
  "Signage & Display": [
    { key: "purpose", label: "Purpose (event, retail, outdoor…)" },
    { key: "size_format", label: "Size / dimensions" },
    { key: "location", label: "Location / mounting environment" },
    { key: "material", label: "Preferred material" },
    { key: "quantity", label: "Quantity" },
    { key: "deadline", label: "Deadline / event date" },
  ],
  "Print-Ready Artwork": [
    { key: "supplier", label: "Which printer / supplier?" },
    { key: "specifications", label: "Print specifications (size, bleed, colour profile, PDF/X)" },
    { key: "quantity", label: "Quantity / run" },
    { key: "delivery_format", label: "Delivery format required" },
    { key: "deadline", label: "Deadline" },
  ],
};

export default function RequestQuote() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [projectType, setProjectType] = useState("");
  const [projectName, setProjectName] = useState("");
  const [answers, setAnswers] = useState({});
  const [products, setProducts] = useState([]);
  const [productList, setProductList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hiddenProjectTypes, setHiddenProjectTypes] = useState([]);
  const [selectedProductCat, setSelectedProductCat] = useState("");
  const [assets, setAssets] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Project types where a specific product catalogue doesn't apply — skip the Products step entirely.
  const PRODUCTS_SKIPPED_FOR = new Set([
    "Logo & Branding",
    "Social Media Graphics",
    "Website Design",
    "Promotional Campaigns",
  ]);
  const skipProducts = PRODUCTS_SKIPPED_FOR.has(projectType);
  const totalSteps = skipProducts ? 4 : 5;

  // Build the list of visible step numbers so we can compute human-friendly labels & progress.
  const visibleSteps = skipProducts ? [1, 2, 4, 5] : [1, 2, 3, 4, 5];
  const visibleIndex = Math.max(0, visibleSteps.indexOf(step)); // 0-based position of `step` in visibleSteps
  const displayStepNumber = visibleIndex + 1;

  useEffect(() => {
    api.get("/products").then((r) => setProductList(r.data));
    api.get("/products/categories").then((r) => setCategories(r.data));
    api.get("/project-types/hidden").then((r) => setHiddenProjectTypes(r.data.hidden || [])).catch(() => {});
  }, []);

  // If the user switches to a "no-products" type after having already selected some, clear them.
  useEffect(() => {
    if (skipProducts && (products.length > 0 || step === 3)) {
      setProducts([]);
      if (step === 3) setStep(4);
    }
  }, [skipProducts, products.length, step]);

  const questions = projectType ? QUESTIONS[projectType] || [] : [];
  const productsFiltered = selectedProductCat ? productList.filter((p) => p.category === selectedProductCat) : productList;

  const toggleProduct = (id) => setProducts(products.includes(id) ? products.filter((p) => p !== id) : [...products, id]);

  const canNext = () => {
    if (step === 1) return !!projectType;
    if (step === 2) return !!projectName.trim();
    return true;
  };

  const goNext = () => {
    // Skip step 3 (Products) for project types where it doesn't apply.
    if (step === 2 && skipProducts) return setStep(4);
    setStep(Math.min(5, step + 1));
  };
  const goBack = () => {
    if (step === 4 && skipProducts) return setStep(2);
    setStep(Math.max(1, step - 1));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs", {
        project_type: projectType, project_name: projectName, products,
        questionnaire: answers, asset_ids: assets.map((a) => a.id),
      });
      toast.success("Request submitted");
      nav(`/jobs/${data.id}`);
    } catch { toast.error("Failed to submit request"); }
    finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="chip mb-3">// new project · step {displayStepNumber} of {totalSteps}</div>
        <h1 className="font-display text-4xl md:text-6xl mb-8">
          {step === 1 && "Choose project type"}
          {step === 2 && "Project details"}
          {step === 3 && "Products (optional)"}
          {step === 4 && "Assets"}
          {step === 5 && "Review & submit"}
        </h1>

        <div className="flex gap-1 mb-10">
          {visibleSteps.map((n, i) => (
            <div key={n} className={`h-2 flex-1 ${i <= visibleIndex ? 'bg-[var(--gc-orange)]' : 'bg-[var(--gc-border-strong)]'}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="step-project-type">
            {Object.entries(CATEGORY_META).filter(([name]) => !hiddenProjectTypes.includes(name)).map(([name, meta]) => {
              const Icon = meta.icon;
              const selected = projectType === name;
              return (
                <button key={name} onClick={() => setProjectType(name)}
                  className={`sticker p-6 text-left transition-all ${selected ? 'sticker-orange' : ''}`}
                  data-testid={`pick-type-${name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                  <div className={`w-full ${meta.tint} p-4 mb-4`}>
                    <Icon size={32} className="text-[var(--gc-navy)]" weight="bold" />
                  </div>
                  <div className="font-display text-xl leading-tight">{name}</div>
                  <div className="tech-label mt-2">{selected ? '✓ SELECTED' : 'SELECT'}</div>
                  {selected && <div className="absolute top-3 right-3"><Check size={20} weight="bold" className="text-[var(--gc-orange)]" /></div>}
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6" data-testid="step-details">
            <div>
              <div className="tech-label mb-2">Project Name *</div>
              <input value={projectName} onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. Autumn Campaign 2026" className="gc-input" data-testid="project-name-input" />
            </div>
            <div className="space-y-4">
              <div className="chip">// questionnaire · {projectType.toLowerCase()}</div>
              {questions.map((q) => (
                <div key={q.key}>
                  <div className="tech-label mb-2">{q.label}</div>
                  <textarea rows={2} value={answers[q.key] || ""} onChange={(e) => setAnswers({ ...answers, [q.key]: e.target.value })}
                    className="gc-input" data-testid={`q-${q.key}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="step-products">
            <p className="text-[var(--gc-text-muted)]">Optionally pick specific products from our catalog. Skip if not sure — we&apos;ll help spec it in the quote.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSelectedProductCat("")}
                className={`px-3 py-1.5 text-xs font-semibold border-2 border-[var(--gc-navy)] ${!selectedProductCat ? 'bg-[var(--gc-navy)] text-white' : 'bg-white hover:bg-[var(--gc-bg-alt)]'}`}>All</button>
              {categories.map((c) => (
                <button key={c} onClick={() => setSelectedProductCat(c)}
                  className={`px-3 py-1.5 text-xs font-semibold border-2 border-[var(--gc-navy)] ${selectedProductCat === c ? 'bg-[var(--gc-navy)] text-white' : 'bg-white hover:bg-[var(--gc-bg-alt)]'}`}
                  data-testid={`prod-cat-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>{c}</button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[500px] overflow-auto p-1">
              {productsFiltered.map((p) => {
                const selected = products.includes(p.id);
                return (
                  <button key={p.id} onClick={() => toggleProduct(p.id)}
                    className={`text-left p-3 border-2 transition-colors ${selected ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)]' : 'border-[var(--gc-navy)] bg-white hover:bg-[var(--gc-bg-alt)]'}`}
                    data-testid={`product-${p.id}`}>
                    <div className="tech-label">{p.category}</div>
                    <div className="text-sm mt-1 font-semibold text-[var(--gc-navy)]">{p.name}</div>
                  </button>
                );
              })}
            </div>
            <div className="tech-label">{products.length} product(s) selected</div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="step-assets">
            <p className="text-[var(--gc-text-muted)]">Upload any brand assets, references, or briefs that support your request. As many files as needed.</p>
            <AssetUploader value={assets} onChange={setAssets} label="Project assets" testid="request-assets" />
          </div>
        )}

        {step === 5 && (
          <div className="space-y-4" data-testid="step-review">
            <div className="sticker p-6 space-y-4">
              <div><div className="tech-label">Type</div><div className="text-lg font-semibold">{projectType}</div></div>
              <div><div className="tech-label">Name</div><div className="text-lg font-semibold">{projectName}</div></div>
              {!skipProducts && (
                <div><div className="tech-label">Products</div><div className="text-sm">{products.length ? products.map(id => productList.find(p => p.id === id)?.name).join(", ") : "—"}</div></div>
              )}
              <div><div className="tech-label">Assets</div><div className="text-sm">{assets.length} file(s)</div></div>
              <div><div className="tech-label">Answers</div><div className="text-xs text-[var(--gc-text-muted)]">{Object.keys(answers).length} / {questions.length} answered</div></div>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-10">
          <button onClick={goBack} disabled={step === 1}
            className="btn-secondary inline-flex items-center gap-2 px-5 py-3 disabled:opacity-30"
            data-testid="wizard-back">
            <ArrowLeft size={16} weight="bold"/> Back
          </button>
          {step < 5 ? (
            <button onClick={goNext} disabled={!canNext()}
              className="btn-primary inline-flex items-center gap-2 px-5 py-3 disabled:opacity-50"
              data-testid="wizard-next">
              Next <ArrowRight size={16} weight="bold"/>
            </button>
          ) : (
            <button onClick={submit} disabled={submitting}
              className="btn-primary inline-flex items-center gap-2 px-5 py-3 disabled:opacity-50"
              data-testid="wizard-submit">
              {submitting ? "Submitting…" : "Submit Request"} <Check size={16} weight="bold"/>
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
